import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import { MandatoryProcessTasksQuery, ProjectionKind } from 'generated/resources/datatable.graphql'
import {
  ProcessesProcessCategoryEnum,
  TasksTaskStatusEnum,
  TasksValueObjectsTaskIdentifierEnum,
} from 'generated/resources/graphql'
import { unescape } from 'lodash'

import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { DueDate } from 'modules/processes/components/MaterializedProcessesTable/NextTaskDueDate'
import { TaskAssignee } from 'modules/tasks/components/TasksList/types'

export type TaskDataTableType =
  MandatoryProcessTasksQuery['tasks']['materializedProcessTasksConnection']['edges'][number]['node']

export type TasksDataTableType = TaskDataTableType[]

export interface TasksFiltersType {
  search: string
  status?: TasksTaskStatusEnum
  authorId?: number
}

const NameProjection = () => ({
  identifier: 'name',
  colSpan: 4,
  template: '{{ name }}',
  kind: ProjectionKind.Custom,
  title: i18n.t('processes.process.overview.tasks_table.name'),
  render: ({ name, completedAt }: { name: string; completedAt: Date }) => {
    return (
      <Text.Normal lineClamp={4} stroked={!!completedAt} weight="medium">
        {unescape(name)}
      </Text.Normal>
    )
  },
})

const AssigneeProjection = () => ({
  identifier: 'assigneesConnection',
  colSpan: 2,
  kind: ProjectionKind.Custom,
  title: i18n.t('processes.process.overview.tasks_table.assignees'),
  template: `
    {%- assign assignees = assigneesConnection.nodes | map: 'fullName'  -%}
    {{assignees | join: ', '}}`,
  render: ({ assigneesConnection }: { assigneesConnection: { nodes?: TaskAssignee[] | null } }) => {
    const assignees = assigneesConnection?.nodes ?? []
    const name = assignees.map((assignee) => assignee.fullName).join(', ')

    return (
      <Text.Normal lineClamp={4} weight="medium">
        {name}
      </Text.Normal>
    )
  },
})

const AffecteeProjection = () => ({
  identifier: 'affecteeName',
  colSpan: 4,
  title: '',
  kind: ProjectionKind.Custom,
  template: `
    {%- assign affectees = affecteesConnection.nodes | map: 'fullName'  -%}
    {{affectees | join: ', '}}`,
  render: ({
    affecteesConnection,
  }: {
    affecteesConnection: { nodes?: TaskAssignee[] | null; totalCount?: number | null }
  }) => {
    const name = affecteesConnection?.nodes?.map((affectee) => affectee.fullName).join(', ')

    return (
      <Text.Normal lineClamp={4} weight="medium">
        {name}
      </Text.Normal>
    )
  },
})

interface ContractDateProjectionProps {
  category: ProcessesProcessCategoryEnum
}

const ContractDateProjection = (props: ContractDateProjectionProps) => ({
  identifier: 'contractDate',
  colSpan: 3,
  kind: ProjectionKind.Custom,
  template: '{{ contractReferenceDate }}',
  render: ({
    contractEndDate,
    contractStartDate,
  }: {
    contractEndDate?: TaskDataTableType['contractEndDate']
    contractStartDate?: TaskDataTableType['contractStartDate']
  }) => {
    const { category } = props

    const title = category === 'onboarding' ? contractStartDate : contractEndDate

    return (
      <Text.Normal lineClamp={2} weight="medium">
        {title ? dateToString(stringToDate(title, 'backend_datetime'), 'ui') : '-'}
      </Text.Normal>
    )
  },
})

const StepNameProjection = () => ({
  identifier: 'stepName',
  colSpan: 3,
  kind: ProjectionKind.Custom,
  title: i18n.t('processes.process.overview.tasks_table.step_name'),
  template: '{{ stepName }}',
  render: ({ stepName }: { stepName?: TaskDataTableType['stepName'] }) => {
    return (
      <Text.Normal lineClamp={2} weight="medium">
        {stepName ? unescape(stepName) : stepName}
      </Text.Normal>
    )
  },
})

const DueDateProjection = () => ({
  identifier: 'dueOn',
  colSpan: 3,
  kind: ProjectionKind.Custom,
  title: i18n.t('processes.process.overview.tasks_table.due_date'),
  template: '{{ dueOn }}',
  render: ({
    dueOn,
    completedAt,
  }: {
    dueOn: TaskDataTableType['dueOn']
    completedAt: TaskDataTableType['completedAt']
  }) => {
    const formattedDueOn = dueOn ? stringToDate(dueOn, 'backend_datetime') : null

    return (
      <Box alignItems="center" flexDirection="row" justifyContent="spaceBetween">
        {completedAt && (
          <Tag ellipsis={false} type="lime">
            {i18n.t('processes.process.employee.table.columns.completed')}
          </Tag>
        )}
        {!completedAt && <DueDate date={formattedDueOn} />}
      </Box>
    )
  },
})

const taskStatusTemplage = `{%- if completedAt -%}
  completed
  {%- else -%}
  pending
  {%- endif -%}`

const TaskStatusProjection = () => ({
  identifier: 'taskStatus',
  hidden: true,
  colSpan: 3,
  kind: ProjectionKind.Custom,
  title: i18n.t('processes.process.overview.tasks_table.status'),
  template: taskStatusTemplage,
  render: ({ completedAt }: { completedAt: TaskDataTableType['completedAt'] }) => {
    const value = completedAt
      ? {
          label: i18n.t('processes.process.overview.tasks_table.statuses.completed'),
          type: Tag.Types.lime,
        }
      : {
          label: i18n.t('processes.process.overview.tasks_table.statuses.pending'),
          type: Tag.Types.champagne,
        }

    return (
      <Box alignItems="center" flexDirection="row" justifyContent="spaceBetween">
        <Tag ellipsis={false} type={value.type}>
          {value.label}
        </Tag>
      </Box>
    )
  },
})

interface QuickActionsSectionProps {
  pathname: (id: number, identifier: TasksValueObjectsTaskIdentifierEnum) => string
}

type ProjectionProps = {
  affecteeTitle: string
  contractStartDateTitle: string | null
} & QuickActionsSectionProps &
  ContractDateProjectionProps

export const projections = (props: ProjectionProps) => {
  const { affecteeTitle, contractStartDateTitle } = props
  const firstProjections = [
    NameProjection(),
    { ...AffecteeProjection(), title: affecteeTitle },
    AssigneeProjection(),
  ]

  const lasProjections = [StepNameProjection(), DueDateProjection(), TaskStatusProjection()]

  const contractProjection = contractStartDateTitle && {
    ...ContractDateProjection(props),
    title: contractStartDateTitle,
  }

  if (contractProjection) {
    return [...firstProjections, contractProjection, ...lasProjections]
  }

  return [...firstProjections, ...lasProjections]
}
